import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, NgZone, ViewChild, computed, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { OrderService, PaymentInclusionPayload } from './core/services/order.service';
import { ActivatedRoute } from '@angular/router';
import JsBarcode from 'jsbarcode';
import { environment } from '../environments/environment';

export interface Payment {
  method: 'pix' | 'card' | 'boleto';
  amount: number;
  feeLabel?: string;
  seq?: string;
  statusCode?: string;
  statusDescription?: string;
  message?: string;
  lastFour?: string;
  installments?: string;
  boletoCode?: string;
  boletoBarcode?: string;
  boletoLine?: string;
  pixKey?: string;
  pixQrImage?: string;
}

type PaymentMethod = Payment['method'];

interface StoredPaymentAsset {
  pixKey?: string | null;
  pixQrImage?: string | null;
  boletoBarcode?: string | null;
  boletoLine?: string | null;
}

interface PaymentAssetModalState {
  open: boolean;
  kind: 'pix' | 'boleto' | null;
  title: string;
  primaryCodeLabel: string;
  primaryCode: string | null;
  boletoBarcode: string | null;
  pixCode: string | null;
  qrImage: string | null;
  pdfBase64: string | null;
  paymentSeq: string | null;
}

interface OrderPaymentCondition {
  codigo?: string;
  descricao?: string;
  parcela_de?: number;
  parcela_ate?: number;
  taxa_minima?: number;
  taxa_maxima?: number;
  parcelas?: OrderCardInstallmentApiItem[];
}

interface OrderCardInstallmentApiItem {
  parcela?: string;
  taxa?: number;
  codigo_cfg?: string;
  codigo_adq?: string;
  adquirente?: string;
}

interface CardInstallmentOption {
  value: string;
  installments: number;
  rate: number;
  cfgCode?: string;
  adqCode?: string;
  acquirer?: string;
  perInstallmentAmount: number;
  totalAmount: number;
  feeAmount: number;
  label: string;
  totalLabel: string;
  feeLabel: string;
}

interface OrderReceiptItem {
  recebimento?: {
    seq?: string;
    status_codigo?: string;
    status_descricao?: string;
    valor?: number;
    valor_pago?: number;
    taxa_valor?: number;
    parcela?: string;
    txid?: string;
    data?: string;
    hora?: string;
    mensagem?: string;
  };
  adquirente?: {
    cfg_codigo?: string;
    adq_codigo?: string;
    adq_descricao?: string;
    boleto_codigo_barras?: string;
    boleto_linha_digitavel?: string;
    boleto_x64?: string;
    pix_qrcode?: string;
    pix_png?: string;
    condicao_pagto_desc?: string;
    condicao_pagto_codigo?: string;
    condicao_pagto_bandeira?: string;
  };
}

interface OrderRegistrationItem {
  cliente?: {
    cgc?: string;
    razao_social?: string;
    nome_fantasia?: string;
  };
  origem?: {
    mensagem?: string;
  };
  solicitacao?: {
    codigo?: string;
  };
}

interface OrderResponse {
  statusCode?: number;
  msg?: string;
  valor_original?: number;
  valor_pago?: number;
  valor_andamento?: number;
  valor_saldo?: number;
  status_codigo?: string;
  status_descricao?: string;
  cadastrais?: OrderRegistrationItem[];
  recebimentos?: OrderReceiptItem[];
  condicao_pagamento?: OrderPaymentCondition[];
}

interface PaymentInclusionResponse {
  statusCode?: number;
  msg?: string;
  message?: string;
  errorMessage?: string;
  payment?: {
    boleto_x64?: string;
    boleto_codigo_barras?: string;
    boleto_linha_digitavel?: string;
    pix_qrcode?: string;
    px_qrcode?: string;
    pix_png?: string;
  };
}

@Component({
  selector: 'app-root',
  templateUrl: './app.html',
  standalone: false,
  styleUrl: './app.scss',
  host: {
    '[style.--checkout-primary]': 'branding.primaryColor',
  },
})
export class App implements AfterViewInit {
  readonly branding = environment.branding;
  @ViewChild('boletoBarcodeSvg') private boletoBarcodeSvg?: ElementRef<SVGSVGElement>;
  @ViewChild('assetModalBoletoBarcodeSvg') private assetModalBoletoBarcodeSvg?: ElementRef<SVGSVGElement>;

  // ── Order config ──────────────────────────────────────────
  private readonly defaultCompanyId = '01';
  recId: string | null = null;
  companyId: string | null = null;
  customerDocument = '';
  customerName = '';
  orderTitle = '';
  requestCode = '';
  orderTotalSig = signal(0);
  orderPaidSig = signal(0);
  orderInProgressSig = signal(0);

  // ── Loading ───────────────────────────────────────────────
  isLoading = true;
  loadError = false;
  loadErrorMessage = 'Verifique sua conexão e tente novamente.';

  // ── State ─────────────────────────────────────────────────
  payments = signal<Payment[]>([]);
  availablePaymentMethods: PaymentMethod[] = ['pix', 'card', 'boleto'];
  activeMethod: PaymentMethod = 'pix';
  cardPaymentCondition: OrderPaymentCondition | null = null;
  isRefreshingPayments = false;
  downloadingBoletoSeq: string | null = null;
  toastMsg = '';
  toastError = false;
  toastVisible = false;
  private toastTimer: any;

  // ── PIX form ──────────────────────────────────────────────
  pixAmount: number | null = null;
  pixAmountDisplay = '';
  pixKey: string | null = null;
  pixQrImage: string | null = null;
  isPixSubmitting = false;

  // ── Card form ─────────────────────────────────────────────
  cardNumber = '';
  cardName = '';
  cardDocument = '';
  cardExpiry = '';
  cardCvv = '';
  cardInstallments = '1';
  cardAmount: number | null = null;
  cardAmountDisplay = '';
  isCardSubmitting = false;
  private readonly cardInstallmentsSig = signal('1');
  private readonly cardAmountSig = signal(0);
  private readonly cardPaymentConditionSig = signal<OrderPaymentCondition | null>(null);

  readonly cardInstallmentOptions = computed<CardInstallmentOption[]>(() => {
    const condition = this.cardPaymentConditionSig();
    const amount = Math.max(0, this.cardAmountSig());
    const installmentItems = Array.isArray(condition?.parcelas) ? condition.parcelas : [];

    if (installmentItems.length > 0) {
      return installmentItems
        .map((item) => this.mapCardInstallmentOption(item, amount))
        .filter((item): item is CardInstallmentOption => item !== null)
        .sort((a, b) => a.installments - b.installments);
    }

    const from = Math.max(1, Number(condition?.parcela_de ?? 1));
    const to = Math.max(from, Number(condition?.parcela_ate ?? from));

    return Array.from({ length: to - from + 1 }, (_, index) => {
      const installments = from + index;
      return this.buildCardInstallmentOption({
        installments,
        rate: 0,
      }, amount);
    });
  });

  readonly paidTotalSig = computed(() => {
    return this.payments()
      .filter((payment) => this.getPaymentStatusTone(payment) === 'success')
      .reduce((acc, payment) => acc + payment.amount, 0);
  });

  readonly inProgressTotalSig = computed(() => {
    const paymentsInProgress = this.payments()
      .filter((payment) => this.getPaymentStatusTone(payment) === 'warning')
      .reduce((acc, payment) => acc + payment.amount, 0);

    if (paymentsInProgress > 0) {
      return paymentsInProgress;
    }

    return this.orderInProgressSig();
  });

  readonly coveredTotalSig = computed(() => this.paidTotalSig() + this.inProgressTotalSig());
  readonly remainingSig = computed(() => Math.max(0, this.orderTotalSig() - this.coveredTotalSig()));
  readonly progressPctSig = computed(() => Math.min(100, (this.coveredTotalSig() / this.orderTotalSig()) * 100));
  readonly paidProgressPctSig = computed(() => Math.min(100, (this.paidTotalSig() / this.orderTotalSig()) * 100));
  readonly inProgressPctSig = computed(() => Math.min(100 - this.paidProgressPctSig(), (this.inProgressTotalSig() / this.orderTotalSig()) * 100));
  readonly isFullyCoveredSig = computed(() => this.remainingSig() === 0);
  readonly hasProcessingCoverageSig = computed(() => this.isFullyCoveredSig() && this.inProgressTotalSig() > 0);
  readonly isPaymentCompleteSig = computed(() => this.isFullyCoveredSig() && !this.hasProcessingCoverageSig());

  readonly selectedCardInstallmentOption = computed<CardInstallmentOption | null>(() => {
    const selected = Number(this.cardInstallmentsSig());
    return this.cardInstallmentOptions().find((option) => option.installments === selected) ?? null;
  });

  // ── Card visual ───────────────────────────────────────────
  cardFocusField: 'number' | 'name' | 'expiry' | 'cvv' | null = null;
  cardBrand: 'visa' | 'mastercard' | 'amex' | 'elo' | 'hipercard' | 'unknown' = 'unknown';

  // ── Boleto form ───────────────────────────────────────────
  boletoAmount: number | null = null;
  boletoAmountDisplay = '';
  boletoCode: string | null = null;
  boletoBarcode: string | null = null;
  boletoPixKey: string | null = null;
  boletoPixQrImage: string | null = null;
  boletoDueDate = '';
  isBoletoSubmitting = false;

  // ── Payment asset modal ───────────────────────────────────
  readonly paymentAssetModalSig = signal<PaymentAssetModalState>({
    open: false,
    kind: null,
    title: '',
    primaryCodeLabel: '',
    primaryCode: null,
    boletoBarcode: null,
    pixCode: null,
    qrImage: null,
    pdfBase64: null,
    paymentSeq: null,
  });
  readonly paymentAssetModalOpenSig = computed(() => this.paymentAssetModalSig().open);
  readonly paymentAssetModalHasBoletoDataSig = computed(() => {
    const modal = this.paymentAssetModalSig();
    return modal.kind === 'boleto' && Boolean(modal.primaryCode || modal.boletoBarcode || modal.qrImage || modal.pixCode);
  });

  constructor(
    private readonly orderService: OrderService,
    private readonly route: ActivatedRoute,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) { }

  ngAfterViewInit(): void {
    this.renderBoletoBarcode();
    this.renderPaymentAssetModalBarcode();
  }

  // ── Computed ──────────────────────────────────────────────
  get paidTotal(): number {
    return this.paidTotalSig();
  }

  get inProgressTotal(): number {
    return this.inProgressTotalSig();
  }

  get coveredTotal(): number {
    return this.coveredTotalSig();
  }

  get remaining(): number {
    return this.remainingSig();
  }

  get progressPct(): number {
    return this.progressPctSig();
  }

  get paidProgressPct(): number {
    return this.paidProgressPctSig();
  }

  get inProgressPct(): number {
    return this.inProgressPctSig();
  }

  get isFullyCovered(): boolean {
    return this.isFullyCoveredSig();
  }

  get hasProcessingCoverage(): boolean {
    return this.hasProcessingCoverageSig();
  }

  get isComplete(): boolean {
    return this.isPaymentCompleteSig();
  }

  isSuccessfulPayment(payment: Payment): boolean {
    return this.getPaymentStatusTone(payment) === 'success';
  }

  get hasRefreshablePayments(): boolean {
    return this.payments().some((payment) => payment.statusCode === '0');
  }

  getPaymentStatusTone(payment: Payment): 'success' | 'warning' | 'danger' | 'neutral' {
    const normalizedStatus = (payment.statusDescription ?? '').trim().toLowerCase();

    if (normalizedStatus.includes('cancel')) {
      return 'neutral';
    }

    if (normalizedStatus.includes('rejeit') || normalizedStatus.includes('recus') || payment.statusCode === '3') {
      return 'danger';
    }

    if (
      normalizedStatus.includes('process') ||
      normalizedStatus.includes('penden') ||
      normalizedStatus.includes('aguard') ||
      payment.statusCode === '0'
    ) {
      return 'warning';
    }

    if (normalizedStatus === 'recebido' || normalizedStatus === 'pago' || payment.statusCode === '1') {
      return 'success';
    }

    return 'neutral';
  }

  getPaymentStatusLabel(payment: Payment): string {
    const normalizedStatus = (payment.statusDescription ?? '').trim().toLowerCase();

    if (normalizedStatus === 'recebido' || normalizedStatus === 'pago') {
      return 'Pago';
    }

    return payment.statusDescription?.trim() || 'Sem status';
  }

  getPaymentAmountPrefix(payment: Payment): string {
    const tone = this.getPaymentStatusTone(payment);
    return tone === 'danger' || tone === 'neutral' ? '' : '+ ';
  }

  getPaymentAmountChipClass(payment: Payment): string {
    return `payment-amount-chip--${this.getPaymentStatusTone(payment)}`;
  }

  getPaymentMessageClass(payment: Payment): string {
    return `payment-status-message--${this.getPaymentStatusTone(payment)}`;
  }

  canOpenBoletoDetails(payment: Payment): boolean {
    return payment.method === 'boleto' && this.getPaymentStatusTone(payment) === 'warning';
  }

  get cardCvvMaxLength(): number {
    return 4;
  }

  get cardCvvPlaceholder(): string {
    return '0000';
  }

  // Divide o número em 4 grupos de 4 dígitos, preenchendo com •
  get cardNumberGroups(): string[] {
    const digits = this.cardNumber.replace(/\s/g, '');
    const groups: string[] = [];
    for (let i = 0; i < 4; i++) {
      const slice = digits.substring(i * 4, i * 4 + 4);
      groups.push(slice.padEnd(4, '•'));
    }
    return groups;
  }

  ngOnInit(): void {
    try {
      this.loadError = false;
      this.loadErrorMessage = 'Verifique sua conexão e tente novamente.';
      const params = this.resolveInitialParams();
      this.recId = params.recId;
      this.companyId = params.companyId;

      if (this.recId && this.companyId) {
        this.hydrateStoredPaymentAssets();
        this.getOrderData();
      } else {
        this.isLoading = false;
        this.loadError = true;
      }
    } catch (ex) {
      this.isLoading = false;
      this.loadError = true;
    }
  }

  // ── Formatters ────────────────────────────────────────────
  fmtBRL(val: number): string {
    return val.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  private roundValue(value: number, decimals = 2): number {
    const factor = 10 ** decimals;
    return Math.round((value + Number.EPSILON) * factor) / factor;
  }

  private roundCurrency(value: number): number {
    return this.roundValue(value, 2);
  }

  private roundCalculation(value: number): number {
    return this.roundValue(value, 6);
  }

  // ── Máscara BRL ───────────────────────────────────────────
  private centsToDisplay(cents: number): string {
    if (!cents) return '';
    return (cents / 100).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  private applyMask(event: Event, maxValue?: number): number {
    const input = event.target as HTMLInputElement;
    const onlyDigits = input.value.replace(/\D/g, '');
    const rawCents = parseInt(onlyDigits || '0', 10);
    const maxCents = maxValue !== undefined ? Math.max(0, Math.round(maxValue * 100)) : null;
    const cents = maxCents !== null ? Math.min(rawCents, maxCents) : rawCents;
    const formatted = this.centsToDisplay(cents);
    input.value = formatted;
    input.setSelectionRange(formatted.length, formatted.length);
    return cents / 100;
  }

  onPixAmountInput(event: Event): void {
    this.pixAmount = this.applyMask(event, this.remaining);
  }

  onCardAmountInput(event: Event): void {
    this.cardAmount = this.applyMask(event, this.remaining);
    this.cardAmountSig.set(this.cardAmount ?? 0);
  }

  onCardInstallmentsChange(value: string): void {
    this.cardInstallments = value;
    this.cardInstallmentsSig.set(value);
  }

  onBoletoAmountInput(event: Event): void {
    this.boletoAmount = this.applyMask(event, this.remaining);
  }

  onCardNameInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.cardName = input.value.toUpperCase();
    input.value = this.cardName;
  }

  onCardDocumentInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, 14);
    const isCnpj = digits.length > 11;

    this.cardDocument = isCnpj
      ? digits
          .replace(/^(\d{2})(\d)/, '$1.$2')
          .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
          .replace(/\.(\d{3})(\d)/, '.$1/$2')
          .replace(/(\d{4})(\d)/, '$1-$2')
      : digits
          .replace(/^(\d{3})(\d)/, '$1.$2')
          .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
          .replace(/\.(\d{3})(\d)/, '.$1-$2');

    input.value = this.cardDocument;
  }

  // ── Detecção de bandeira ──────────────────────────────────
  private detectBrand(num: string): void {
    const n = num.replace(/\s/g, '');

    if (/^4/.test(n)) {
      this.cardBrand = 'visa';
    } else if (/^(5[1-5]|2(2[2-9][1-9]|[3-6]\d{2}|7[01]\d|720))/.test(n)) {
      this.cardBrand = 'mastercard';
    } else if (/^3[47]/.test(n)) {
      this.cardBrand = 'amex';
    } else if (/^(4011(78|79)|43(1274)|438935|45(1416|7393|7631|7632)|504175|627780|636(297|368)|(506699|5067[0-6]\d|50677[0-8])|(6500(3[1-3]|3[5-9]|4\d|5[01]))|(6504[1-3]\d)|(6504(8[5-9]|9\d)|6505[0-2]\d|65053[0-8])|(65054[1-9]|6505[5-8]\d|65059[0-8])|(65070\d|65071[0-8])|(65072[0-7])|(65090[1-9]|65091\d|650920)|(6516(5[2-9]|[6-7]\d))|(6550(0\d|1\d))|(65502[1-9]|6550[3-4]\d|65505[0-8]))/.test(n)) {
      this.cardBrand = 'elo';
    } else if (/^(384100|384140|384160|606282|637095|637568|637599|637609|637612)/.test(n)) {
      this.cardBrand = 'hipercard';
    } else {
      this.cardBrand = 'unknown';
    }
  }

  // ── Method tab ────────────────────────────────────────────
  selectMethod(method: PaymentMethod): void {
    if (!this.availablePaymentMethods.includes(method)) {
      return;
    }

    this.activeMethod = method;
    this.hydrateStoredPaymentAssets();
    this.queueBoletoBarcodeRender();
  }

  // ── Use remaining ─────────────────────────────────────────
  useRemaining(): void {
    const val = parseFloat(this.remaining.toFixed(2));
    const cents = Math.round(val * 100);
    const formatted = this.centsToDisplay(cents);

    if (this.activeMethod === 'pix') {
      this.pixAmount = val;
      this.pixAmountDisplay = formatted;
    } else if (this.activeMethod === 'card') {
      this.cardAmount = val;
      this.cardAmountDisplay = formatted;
      this.cardAmountSig.set(val);
    } else {
      this.boletoAmount = val;
      this.boletoAmountDisplay = formatted;
    }
  }

  // ── Add payment ───────────────────────────────────────────
  addPayment(): void {
    if (this.activeMethod === 'pix') {
      this.confirmPixReceipt();
    } else if (this.activeMethod === 'card') {
      this.confirmCardPayment();
    } else {
      this.confirmBoletoPayment();
    }
  }

  private confirmPixPayment(): void {
    if (this.isPixSubmitting) {
      return;
    }

    const val = this.pixAmount ?? 0;
    if (!val || val <= 0)            { this.showToast('Informe um valor válido', true); return; }
    if (val > this.remaining + 0.01) { this.showToast('Valor excede o restante', true); return; }

    this.isPixSubmitting = true;
    const amount = Math.min(val, this.remaining);
    this.pixKey = null;
    this.pixQrImage = null;

    this.includePayment({
      forma_pagto: 'PIX',
      valor: amount
    }, (response) => {
      const payment = response?.payment;
      this.pixKey =
        payment?.pix_qrcode ||
        payment?.px_qrcode ||
        null;
      this.pixQrImage = payment?.pix_png || null;
      this.isPixSubmitting = false;

      this.persistPaymentAsset('pix', {
        pixKey: this.pixKey,
        pixQrImage: this.pixQrImage
      });

      if (!this.pixKey && !this.pixQrImage) {
        this.showToast('Nao foi possivel obter o QR Code PIX', true);
        this.cdr.detectChanges();
        return;
      }

      this.showToast(response?.msg || response?.message || 'QR Code PIX gerado!');
      this.cdr.detectChanges();
    }, () => {
      this.isPixSubmitting = false;
      this.cdr.detectChanges();
    }, false);
  }

  private confirmPixReceipt(): void {
    this.showToast('A confirmacao do pagamento PIX sera integrada em breve.');
  }

  newPixPayment(): void {
    this.pixKey = null;
    this.pixQrImage = null;
    this.pixAmount = null;
    this.pixAmountDisplay = '';
  }

  private confirmCardPayment(): void {
    if (this.isCardSubmitting) {
      return;
    }

    const num = this.cardNumber.replace(/\s/g, '');
    const val = this.cardAmount ?? 0;

    if (num.length < 13)             { this.showToast('Número do cartão inválido', true); return; }
    if (!this.cardName.trim())       { this.showToast('Informe o nome no cartão', true); return; }
    if (this.cardDocument.replace(/\D/g, '').length < 11) { this.showToast('CPF/CNPJ inválido', true); return; }
    if (this.cardExpiry.length < 5)  { this.showToast('Validade inválida', true); return; }
    if (this.cardCvv.length < 3 || this.cardCvv.length > 4) { this.showToast('CVV inválido', true); return; }
    if (!val || val <= 0)            { this.showToast('Informe um valor válido', true); return; }
    if (val > this.remaining + 0.01) { this.showToast('Valor excede o restante', true); return; }
    if (!this.customerName) {
      this.showToast('Não foi possível identificar os dados do cliente', true);
      return;
    }

    this.isCardSubmitting = true;
    const baseAmount = Math.min(val, this.remaining);
    const selectedInstallmentOption = this.selectedCardInstallmentOption();
    const finalAmount = selectedInstallmentOption?.totalAmount ?? baseAmount;
    const [month = '', year = ''] = this.cardExpiry.split('/');
    const fullYear = year.length === 2 ? `20${year}` : year;

    this.includePayment({
      forma_pagto: 'CC',
      valor: Number(finalAmount.toFixed(2)),
      parcela: this.cardInstallments,
      taxa: selectedInstallmentOption?.feeAmount ?? 0,
      taxa_percentual: selectedInstallmentOption?.rate ?? 0,
      codigo_cfg: selectedInstallmentOption?.cfgCode,
      codigo_adq: selectedInstallmentOption?.adqCode,
      adquirente: selectedInstallmentOption?.acquirer,
      dados_cartao: [{
        numero: num,
        cvv: this.cardCvv,
        vencto_mes: month,
        vencto_ano: fullYear,
        documento_cliente: this.cardDocument.replace(/\D/g, ''),
        nome_cliente: this.cardName.trim()
      }]
    }, () => {
      this.cardNumber = '';
      this.cardName = '';
      this.cardDocument = '';
      this.cardExpiry = '';
      this.cardCvv = '';
      this.cardAmount = null;
      this.cardAmountSig.set(0);
      this.cardAmountDisplay = '';
      this.cardBrand = 'unknown';
      this.isCardSubmitting = false;
      this.showToast('Pagamento com cartão enviado!');
    }, () => {
      this.isCardSubmitting = false;
    });
  }

  private confirmBoletoPayment(): void {
    this.showToast('A confirmacao do pagamento por boleto sera integrada em breve.');
  }

  newBoletoPayment(amountInput?: HTMLInputElement): void {
    this.boletoCode = null;
    this.boletoBarcode = null;
    this.boletoPixKey = null;
    this.boletoPixQrImage = null;
    this.boletoDueDate = '';
    this.boletoAmount = null;
    this.boletoAmountDisplay = '';
    if (amountInput) {
      amountInput.value = '';
    }
    this.queueBoletoBarcodeRender();
    this.cdr.detectChanges();
  }

  reopenPixQr(source: 'pix' | 'boleto' = 'pix', payment?: Payment): void {
    const asset = this.readPaymentAsset(source);
    const pixKey = asset?.pixKey || payment?.pixKey || null;
    const pixQrImage = asset?.pixQrImage || payment?.pixQrImage || null;

    if (!pixKey && !pixQrImage) {
      this.showToast('Nenhum QR Code PIX disponivel para visualizacao', true);
      return;
    }

    this.openPaymentAssetModal({
      kind: 'pix',
      title: 'QR Code PIX',
      primaryCodeLabel: 'Chave copia e cola',
      primaryCode: pixKey,
      pixCode: pixKey,
      qrImage: pixQrImage
    });
  }

  openBoletoDetails(payment: Payment): void {
    if (!payment.seq) {
      this.openBoletoDetailsFromPayment(payment);
      return;
    }

    if (!this.recId || !this.companyId) {
      this.openBoletoDetailsFromPayment(payment);
      return;
    }

    this.downloadingBoletoSeq = payment.seq;

    this.orderService.getOrderData(this.recId, this.companyId, payment.seq).subscribe({
      next: (response: OrderResponse & PaymentInclusionResponse) => {
        const receiptMatch = response?.recebimentos?.find(
          (item) => item?.recebimento?.seq?.trim() === payment.seq
        );
        const pdfBase64 = this.normalizeFieldValue(receiptMatch?.adquirente?.boleto_x64);
        this.downloadingBoletoSeq = null;
        this.openBoletoDetailsFromPayment(payment, receiptMatch, pdfBase64);
      },
      error: () => {
        this.downloadingBoletoSeq = null;
        this.openBoletoDetailsFromPayment(payment);
      }
    });
  }

  private openBoletoDetailsFromPayment(payment: Payment, receipt?: OrderReceiptItem, pdfBase64?: string | null): void {
    const asset = this.readPaymentAsset('boleto');
    const boletoLine =
      this.normalizeFieldValue(receipt?.adquirente?.boleto_linha_digitavel) ||
      asset?.boletoLine ||
      payment.boletoLine ||
      null;
    const boletoBarcode =
      this.normalizeFieldValue(receipt?.adquirente?.boleto_codigo_barras) ||
      asset?.boletoBarcode ||
      payment.boletoBarcode ||
      null;
    const pixCode =
      this.normalizeFieldValue(receipt?.adquirente?.pix_qrcode) ||
      asset?.pixKey ||
      payment.pixKey ||
      null;
    const qrImage =
      this.normalizeFieldValue(receipt?.adquirente?.pix_png) ||
      asset?.pixQrImage ||
      payment.pixQrImage ||
      null;

    if (!boletoLine && !boletoBarcode && !pixCode && !qrImage) {
      this.showToast('Nao foi possivel obter os dados do boleto', true);
      return;
    }

    this.openPaymentAssetModal({
      kind: 'boleto',
      title: 'Boleto bancario',
      primaryCodeLabel: boletoLine ? 'Linha digitavel' : 'Codigo de barras',
      primaryCode: boletoLine || boletoBarcode,
      boletoBarcode,
      pixCode,
      qrImage,
      pdfBase64: pdfBase64 || null,
      paymentSeq: payment.seq || null,
    });
  }

  // ── Finalize ──────────────────────────────────────────────
  finalize(): void {
    this.showToast('🎉 Pedido finalizado com sucesso!');
  }

  // ── Copy PIX ─────────────────────────────────────────────
  copyPix(): void {
    if (!this.pixKey) {
      return;
    }

    navigator.clipboard.writeText(this.pixKey).catch(() => {});
    this.showToast('Chave PIX copiada!');
  }

  copyPaymentAssetCode(value: string | null, successMessage = 'Codigo copiado!'): void {
    if (!value) {
      return;
    }

    navigator.clipboard.writeText(value).catch(() => {});
    this.showToast(successMessage);
  }

  downloadBoletoPdfFromModal(): void {
    const modal = this.paymentAssetModalSig();

    if (!modal.pdfBase64 || !modal.paymentSeq) {
      this.showToast('Nao foi possivel obter o PDF do boleto', true);
      return;
    }

    this.downloadBase64File(modal.pdfBase64, `boleto-${modal.paymentSeq}.pdf`, 'application/pdf')
      .then((downloaded) => {
        if (!downloaded) {
          this.showToast('Nao foi possivel baixar o PDF do boleto', true);
          return;
        }

        this.showToast('Boleto baixado!');
      })
      .catch(() => {
        this.showToast('Nao foi possivel baixar o PDF do boleto', true);
      });
  }

  // ── Input masks ───────────────────────────────────────────
  onCardNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let val = input.value.replace(/\D/g, '').substring(0, 16);
    this.cardNumber = val.replace(/(.{4})/g, '$1 ').trim();
    this.detectBrand(val);
  }

  onCardExpiryInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let val = input.value.replace(/\D/g, '').substring(0, 4);
    if (val.length >= 2) val = val.slice(0, 2) + '/' + val.slice(2);
    this.cardExpiry = val;
  }

  onCardCvvInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.cardCvv = input.value.replace(/\D/g, '').substring(0, this.cardCvvMaxLength);
    input.value = this.cardCvv;
  }

  // ── Toast ─────────────────────────────────────────────────
  showToast(msg: string, isError = false): void {
    this.toastMsg = msg;
    this.toastError = isError;
    this.toastVisible = true;
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => { this.toastVisible = false; }, 2800);
  }

  generateQRCode() {
    this.pixQrImage = null;
    this.confirmPixPayment();
  }

  generateBoleto(): void {
    if (this.isBoletoSubmitting) {
      return;
    }

    const val = this.boletoAmount ?? 0;

    if (!val || val <= 0) {
      this.showToast('Informe um valor válido', true);
      return;
    }

    if (val > this.remaining + 0.01) {
      this.showToast('Valor excede o restante', true);
      return;
    }

    this.isBoletoSubmitting = true;
    this.boletoCode = null;
    this.boletoBarcode = null;
    this.boletoPixKey = null;
    this.boletoPixQrImage = null;

    const amount = Math.min(val, this.remaining);
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 3);
    this.boletoDueDate = dueDate.toLocaleDateString('pt-BR');

    this.includePayment({
      forma_pagto: 'BOL',
      valor: amount
    }, (response) => {
      const payment = response?.payment;
      this.boletoBarcode = this.normalizeFieldValue(payment?.boleto_codigo_barras);
      this.boletoCode = this.normalizeFieldValue(payment?.boleto_linha_digitavel) || this.boletoBarcode;
      this.boletoPixKey = this.normalizeFieldValue(payment?.pix_qrcode) || this.normalizeFieldValue(payment?.px_qrcode);
      this.boletoPixQrImage = this.normalizeFieldValue(payment?.pix_png);
      this.isBoletoSubmitting = false;

      this.persistPaymentAsset('boleto', {
        boletoBarcode: this.boletoBarcode,
        boletoLine: this.boletoCode,
        pixKey: this.boletoPixKey,
        pixQrImage: this.boletoPixQrImage
      });
      this.queueBoletoBarcodeRender();

      if (!this.boletoCode && !this.boletoBarcode) {
        this.showToast('Nao foi possivel obter a linha digitavel do boleto', true);
        this.cdr.detectChanges();
        return;
      }

      this.showToast(response?.msg || response?.message || 'Boleto gerado!');
      this.cdr.detectChanges();
    }, () => {
      this.isBoletoSubmitting = false;
      this.cdr.detectChanges();
    }, false);
  }

  copyBoleto(): void {
    const boletoValue = this.boletoCode ?? this.boletoBarcode;

    if (!boletoValue) {
      return;
    }

    navigator.clipboard.writeText(boletoValue).catch(() => {});
    this.showToast('Código do boleto copiado!');
  }

  closePaymentAssetModal(): void {
    this.paymentAssetModalSig.set({
      open: false,
      kind: null,
      title: '',
      primaryCodeLabel: '',
      primaryCode: null,
      boletoBarcode: null,
      pixCode: null,
      qrImage: null,
      pdfBase64: null,
      paymentSeq: null,
    });
  }

  private openPaymentAssetModal(config: {
    kind: 'pix' | 'boleto';
    title: string;
    primaryCodeLabel: string;
    primaryCode: string | null;
    boletoBarcode?: string | null;
    pixCode?: string | null;
    qrImage: string | null;
    pdfBase64?: string | null;
    paymentSeq?: string | null;
  }): void {
    this.paymentAssetModalSig.set({
      open: true,
      kind: config.kind,
      title: config.title,
      primaryCodeLabel: config.primaryCodeLabel,
      primaryCode: config.primaryCode,
      boletoBarcode: config.boletoBarcode || null,
      pixCode: config.pixCode || null,
      qrImage: config.qrImage,
      pdfBase64: config.pdfBase64 || null,
      paymentSeq: config.paymentSeq || null,
    });

    if (config.kind === 'boleto') {
      this.queuePaymentAssetModalBarcodeRender();
    }
  }

  private getPaymentAssetStorageKey(method: 'pix' | 'boleto'): string | null {
    if (!this.recId || !this.companyId) {
      return null;
    }

    return `checkout:${this.companyId}:${this.recId}:${method}`;
  }

  private persistPaymentAsset(method: 'pix' | 'boleto', asset: StoredPaymentAsset): void {
    const storageKey = this.getPaymentAssetStorageKey(method);

    if (!storageKey) {
      return;
    }

    sessionStorage.setItem(storageKey, JSON.stringify(asset));
  }

  private readPaymentAsset(method: 'pix' | 'boleto'): StoredPaymentAsset | null {
    const storageKey = this.getPaymentAssetStorageKey(method);

    if (!storageKey) {
      return null;
    }

    const raw = sessionStorage.getItem(storageKey) || localStorage.getItem(storageKey);

    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as StoredPaymentAsset;
    } catch {
      return null;
    }
  }

  private async downloadBase64File(base64: string, fileName: string, mimeType: string): Promise<boolean> {
    try {
      const sanitizedBase64 = base64
        .replace(/^data:[^;]+;base64,/, '')
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .replace(/[^A-Za-z0-9+/=]/g, '');
      const paddedBase64 = sanitizedBase64.padEnd(
        sanitizedBase64.length + ((4 - (sanitizedBase64.length % 4)) % 4),
        '='
      );
      const byteCharacters = atob(paddedBase64);
      const bytes = new Uint8Array(byteCharacters.length);

      for (let index = 0; index < byteCharacters.length; index++) {
        bytes[index] = byteCharacters.charCodeAt(index);
      }

      const blob = new Blob([bytes.buffer], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');

      anchor.href = url;
      anchor.download = fileName;
      anchor.rel = 'noopener noreferrer';
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      await new Promise((resolve) => setTimeout(resolve, 900));
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      return true;
    } catch {
      return false;
    }
  }

  private hydrateStoredPaymentAssets(): void {
    const pixAsset = this.readPaymentAsset('pix');
    const boletoAsset = this.readPaymentAsset('boleto');

    if (pixAsset) {
      this.pixKey = pixAsset.pixKey ?? null;
      this.pixQrImage = pixAsset.pixQrImage ?? null;
    }

    if (boletoAsset) {
      this.boletoBarcode = boletoAsset.boletoBarcode ?? null;
      this.boletoCode = boletoAsset.boletoLine ?? boletoAsset.boletoBarcode ?? null;
      this.boletoPixKey = boletoAsset.pixKey ?? null;
      this.boletoPixQrImage = boletoAsset.pixQrImage ?? null;
    }

    this.queueBoletoBarcodeRender();
  }

  private queueBoletoBarcodeRender(): void {
    queueMicrotask(() => {
      this.ngZone.runOutsideAngular(() => {
        requestAnimationFrame(() => this.renderBoletoBarcode());
      });
    });
  }

  private queuePaymentAssetModalBarcodeRender(): void {
    queueMicrotask(() => {
      this.ngZone.runOutsideAngular(() => {
        requestAnimationFrame(() => this.renderPaymentAssetModalBarcode());
      });
    });
  }

  private renderBoletoBarcode(): void {
    const svg = this.boletoBarcodeSvg?.nativeElement;
    const barcodeValue = this.boletoBarcode ?? this.boletoCode;

    if (!svg) {
      return;
    }

    if (!barcodeValue) {
      svg.innerHTML = '';
      return;
    }

    try {
      JsBarcode(svg, barcodeValue, {
        format: 'ITF',
        displayValue: false,
        margin: 0,
        width: 1.35,
        height: 52,
        background: '#ffffff',
        lineColor: '#111827'
      });
    } catch {
      svg.innerHTML = '';
    }
  }

  private renderPaymentAssetModalBarcode(): void {
    const svg = this.assetModalBoletoBarcodeSvg?.nativeElement;
    const modal = this.paymentAssetModalSig();
    const barcodeValue = modal.boletoBarcode ?? modal.primaryCode;

    if (!svg) {
      return;
    }

    if (!modal.open || modal.kind !== 'boleto' || !barcodeValue) {
      svg.innerHTML = '';
      return;
    }

    try {
      JsBarcode(svg, barcodeValue, {
        format: 'ITF',
        displayValue: false,
        margin: 0,
        width: 1.35,
        height: 52,
        background: '#ffffff',
        lineColor: '#111827'
      });
    } catch {
      svg.innerHTML = '';
    }
  }

  private normalizeFieldValue(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const normalized = value.trim();
    return normalized ? normalized : null;
  }

  private normalizeApiPayload(payload: unknown): PaymentInclusionResponse & { errorMessage?: string; message?: string } | null {
    if (!payload) {
      return null;
    }

    if (typeof payload === 'object' && payload !== null && 'body' in payload) {
      return this.normalizeApiPayload((payload as { body?: unknown }).body);
    }

    if (typeof payload === 'string') {
      try {
        return JSON.parse(payload);
      } catch {
        return { message: payload };
      }
    }

    if (typeof payload === 'object') {
      return payload as PaymentInclusionResponse & { errorMessage?: string; message?: string };
    }

    return null;
  }

  private extractApiErrorMessage(error: unknown): string | null {
    const errorResponse = error as HttpErrorResponse;
    const payload = this.normalizeApiPayload(errorResponse?.error ?? error);
    return payload?.errorMessage || payload?.message || null;
  }

  private includePayment(
    payload: PaymentInclusionPayload,
    onSuccess: (response: PaymentInclusionResponse | null) => void,
    onError: () => void,
    refreshAfterSuccess = true
  ): void {
    if (!this.recId || !this.companyId) {
      this.showToast('Não foi possível identificar os dados do pedido', true);
      onError();
      return;
    }

    this.orderService.includePayment(this.recId, this.companyId, payload).subscribe({
      next: (response: unknown) => {
        this.ngZone.run(() => {
          const normalizedResponse = this.normalizeApiPayload(response) as PaymentInclusionResponse | null;
          onSuccess(normalizedResponse);
          if (refreshAfterSuccess) {
            this.getOrderData(true);
          }
          this.cdr.detectChanges();
        });
      },
      error: (error: HttpErrorResponse) => {
        this.ngZone.run(() => {
          onError();
          const apiMessage = this.extractApiErrorMessage(error);
          this.showToast(apiMessage || 'Não foi possível incluir o pagamento', true);
          this.cdr.detectChanges();
        });
      }
    });
  }

  refreshPayments(): void {
    if (this.isRefreshingPayments || this.isLoading) {
      return;
    }

    this.getOrderData(true, true);
  }

  private createFakeBoletoCode(): string {
    const blocks = [11, 11, 11, 11];

    return blocks
      .map((size) => Array.from({ length: size }, () => Math.floor(Math.random() * 10)).join(''))
      .join(' ');
  }

  private getParamValue(...keys: string[]): string | null {
    const routeParams = this.route.snapshot.queryParamMap;
    const searchParams = new URLSearchParams(window.location.search);

    for (const key of keys) {
      const routeValue = routeParams.get(key);
      if (routeValue) {
        return routeValue;
      }

      const searchValue = searchParams.get(key);
      if (searchValue) {
        return searchValue;
      }
    }

    return null;
  }

  private resolveInitialParams(): { recId: string | null; companyId: string | null } {
    const directRecId = this.getParamValue('idrec', 'IDREC');
    const directCompanyId = this.getParamValue('empresa', 'EMPRESA', 'companyId', 'COMPANYID');

    if (directRecId && directCompanyId) {
      return {
        recId: directRecId,
        companyId: directCompanyId
      };
    }

    const legacyRec = this.getParamValue('rec', 'REC');
    if (!legacyRec) {
      return {
        recId: directRecId,
        companyId: directCompanyId
      };
    }

    try {
      return {
        recId: atob(legacyRec),
        companyId: directCompanyId ?? this.defaultCompanyId
      };
    } catch {
      return {
        recId: legacyRec,
        companyId: directCompanyId ?? this.defaultCompanyId
      };
    }
  }

  private mapPaymentMethod(code?: string): PaymentMethod {
    if (code === 'PIX') {
      return 'pix';
    }

    if (code === 'BOL' || code === 'BOLETO') {
      return 'boleto';
    }

    return 'card';
  }

  private mapAvailablePaymentMethods(order: OrderResponse): PaymentMethod[] {
    const conditions = Array.isArray(order.condicao_pagamento) ? order.condicao_pagamento : [];
    const methods = new Set<PaymentMethod>();

    for (const condition of conditions) {
      const code = condition?.codigo?.trim();

      if (code === 'PIX') {
        methods.add('pix');
      } else if (code === 'CC') {
        methods.add('card');
      } else if (code === 'BOL' || code === 'BOLETO') {
        methods.add('boleto');
      }
    }

    return methods.size > 0 ? Array.from(methods) : ['pix', 'card', 'boleto'];
  }

  private resolveCardPaymentCondition(order: OrderResponse): void {
    const conditions = Array.isArray(order.condicao_pagamento) ? order.condicao_pagamento : [];
    this.cardPaymentCondition = conditions.find((condition) => condition?.codigo?.trim() === 'CC') ?? null;
    this.cardPaymentConditionSig.set(this.cardPaymentCondition);
    this.syncCardInstallments();
  }

  private syncActiveMethod(): void {
    if (this.availablePaymentMethods.includes(this.activeMethod)) {
      return;
    }

    this.activeMethod = this.availablePaymentMethods[0] ?? 'pix';
  }

  private syncCardInstallments(): void {
    const options = this.cardInstallmentOptions();
    const selected = Number(this.cardInstallments);

    if (options.length === 0) {
      this.cardInstallments = '1';
      this.cardInstallmentsSig.set('1');
      return;
    }

    if (!options.some((option) => option.installments === selected)) {
      const nextValue = options[0]?.value ?? '1';
      this.cardInstallments = nextValue;
      this.cardInstallmentsSig.set(nextValue);
    }
  }

  private mapCardInstallmentOption(item: OrderCardInstallmentApiItem, amount: number): CardInstallmentOption | null {
    const installments = Number(item?.parcela ?? 0);

    if (!Number.isFinite(installments) || installments <= 0) {
      return null;
    }

    return this.buildCardInstallmentOption({
      installments,
      rate: Number(item?.taxa ?? 0),
      cfgCode: item?.codigo_cfg?.trim() || undefined,
      adqCode: item?.codigo_adq?.trim() || undefined,
      acquirer: item?.adquirente?.trim() || undefined,
    }, amount);
  }

  private buildCardInstallmentOption(
    option: {
      installments: number;
      rate: number;
      cfgCode?: string;
      adqCode?: string;
      acquirer?: string;
    },
    amount: number
  ): CardInstallmentOption {
    const installments = option.installments;
    const normalizedRate = Math.max(0, option.rate);
    const baseInstallmentAmount = installments > 0 ? this.roundCalculation(amount / installments) : 0;
    const feePerInstallment = this.roundCalculation(baseInstallmentAmount * (normalizedRate / 100));
    const precisePerInstallmentAmount = this.roundCalculation(baseInstallmentAmount + feePerInstallment);
    const perInstallmentAmount = this.roundCurrency(precisePerInstallmentAmount);
    const totalAmount = this.roundCurrency(precisePerInstallmentAmount * installments);
    const feeAmount = this.roundCurrency(Math.max(0, totalAmount - amount));
    const hasAmount = amount > 0;
    const label = hasAmount
      ? `${installments}x de R$ ${this.fmtBRL(perInstallmentAmount)}`
      : `${installments}x`;
    const totalLabel = hasAmount ? `R$ ${this.fmtBRL(totalAmount)}` : '--';
    const feeLabel = hasAmount
      ? (feeAmount > 0 ? `R$ ${this.fmtBRL(feeAmount)}` : 'Sem taxa')
      : 'Informe valor';

    return {
      value: String(installments),
      installments,
      rate: normalizedRate,
      cfgCode: option.cfgCode,
      adqCode: option.adqCode,
      acquirer: option.acquirer,
      perInstallmentAmount,
      totalAmount,
      feeAmount,
      label,
      totalLabel,
      feeLabel,
    };
  }

  private mapOrderReceipts(order: OrderResponse): Payment[] {
    const receipts = Array.isArray(order.recebimentos) ? [...order.recebimentos].reverse() : [];

    return receipts
      .filter((item) => {
        const receipt = item?.recebimento;
        const paymentCode = item?.adquirente?.condicao_pagto_codigo?.trim();
        const amount = Number(receipt?.valor ?? receipt?.valor_pago ?? 0);
        const status = receipt?.status_descricao?.trim();
        const message = receipt?.mensagem?.trim();

        return Boolean(paymentCode || amount > 0 || (status && status !== '-') || message);
      })
      .map((item) => {
        const amount = Number(item.recebimento?.valor ?? item.recebimento?.valor_pago ?? 0);
        const feeAmount = Number(item.recebimento?.taxa_valor ?? 0);
        const payment: Payment = {
          method: this.mapPaymentMethod(item.adquirente?.condicao_pagto_codigo),
          amount,
          seq: item.recebimento?.seq?.trim() || undefined,
          statusCode: item.recebimento?.status_codigo?.trim() || undefined,
          statusDescription: item.recebimento?.status_descricao?.trim() || undefined,
          message: item.recebimento?.mensagem?.trim() || undefined,
          pixKey: item.adquirente?.pix_qrcode?.trim() || undefined,
          pixQrImage: item.adquirente?.pix_png?.trim() || undefined,
        };

        if (payment.method === 'card') {
          payment.installments = item.recebimento?.parcela?.trim() || undefined;
        }

        if (payment.method === 'boleto') {
          payment.boletoBarcode = item.adquirente?.boleto_codigo_barras?.trim() || undefined;
          payment.boletoLine = item.adquirente?.boleto_linha_digitavel?.trim() || undefined;
          payment.boletoCode = payment.boletoLine || payment.boletoBarcode || item.recebimento?.txid?.trim() || undefined;
        }

        if ((payment.method === 'card' || payment.method === 'boleto') && feeAmount > 0) {
          payment.feeLabel = `Taxa: R$ ${this.fmtBRL(feeAmount)}`;
        }

        return payment;
      });
  }

  private getOrderData(silentRefresh = false, search = false) {
    if ((!this.recId || this.recId === '') || (!this.companyId || this.companyId === '')) {
      this.loadError = true;
      this.isLoading = false;
      this.loadErrorMessage = 'Nao foi possivel comunicar com o servidor.';
      this.showToast('Não foi possível buscar os dados do Pedido!', true);
      return;
    }

    if (silentRefresh) {
      this.isRefreshingPayments = true;
    } else {
      this.isLoading = true;
    }

    this.orderService.getOrderData(this.recId, this.companyId, undefined, search).subscribe({
      next: (order: OrderResponse) => {
        this.ngZone.run(() => {
          if (order?.statusCode === 404) {
            this.loadError = true;
            this.isLoading = false;
            this.isRefreshingPayments = false;
            this.loadErrorMessage = 'Nao foi possivel comunicar com o servidor.';
            this.showToast('Nao foi possivel comunicar com o servidor.', true);
            this.cdr.detectChanges();
            return;
          }

          const registration = order?.cadastrais?.[0];
          const customer = registration?.cliente;
          this.orderTotalSig.set(Number(order?.valor_original ?? 0));
          this.orderPaidSig.set(Number(order?.valor_pago ?? 0));
          this.orderInProgressSig.set(Number(order?.valor_andamento ?? 0));
          this.orderTitle = registration?.origem?.mensagem?.trim() || '';
          this.requestCode = registration?.solicitacao?.codigo?.trim() || '';
          this.customerDocument = customer?.cgc?.trim() || '';
          this.customerName = customer?.nome_fantasia?.trim() || customer?.razao_social?.trim() || '';
          this.availablePaymentMethods = this.mapAvailablePaymentMethods(order ?? {});
          this.resolveCardPaymentCondition(order ?? {});
          this.syncActiveMethod();
          this.payments.set(this.mapOrderReceipts(order ?? {}));
          this.loadError = false;
          this.isLoading = false;
          this.isRefreshingPayments = false;
          this.cdr.detectChanges();
        });
      },
      error: (error: HttpErrorResponse) => {
        this.ngZone.run(() => {
          const apiMessage = this.extractApiErrorMessage(error);
          this.loadError = true;
          this.isLoading = false;
          this.isRefreshingPayments = false;
          this.loadErrorMessage = apiMessage || (error.status === 404
            ? 'Nao foi possivel comunicar com o servidor.'
            : 'Verifique sua conexão e tente novamente.');
          this.showToast(this.loadErrorMessage, true);
          this.cdr.detectChanges();
        });
      }
    });
  }
}
